import { useState, useCallback } from "react";
import { usePageTour } from "@/hooks/use-page-tour";
import { SpotlightOverlay } from "./SpotlightOverlay";

interface PageTourProps {
  onboardingActive: boolean;
}

/**
 * Per-page first-visit spotlight tour.
 * Automatically triggers when a center manager or tutor visits a page
 * for the first time.
 */
export function PageTour({ onboardingActive }: PageTourProps) {
  const { isActive, steps, completeTour } = usePageTour(onboardingActive);
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      setCurrentStep(0);
      completeTour();
    }
  }, [currentStep, steps.length, completeTour]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  }, [currentStep]);

  const handleClose = useCallback(() => {
    setCurrentStep(0);
    completeTour();
  }, [completeTour]);

  // Reset step when tour activates for a new page
  if (!isActive) return null;

  return (
    <SpotlightOverlay
      steps={steps}
      currentStep={currentStep}
      onNext={handleNext}
      onPrev={handlePrev}
      onClose={handleClose}
    />
  );
}
